import dayjs from "dayjs";

import { PICKUP_ADDRESS_ID } from "~/constants/warehouse";
import { supabase } from "~/integrations/supabase/client";
import type {
  CreateQuotationInput,
  CreateQuotationItemInput,
  Quotation,
  QuotationItem,
  QuotationStatus,
  QuotationWithDetails,
  UpdateQuotationInput,
} from "~/types/quotation";

const IVA_RATE = 0.19;

/**
 * Calculate total amount and price with IVA from items
 */
function calculateTotals(items: CreateQuotationItemInput[]): {
  totalAmount: number;
  priceWithIva: number;
} {
  const totalAmount = items.reduce((sum, item) => {
    const lineTotal =
      item.quantity * item.unit_price * (1 - (item.discount_percent || 0) / 100);
    return sum + lineTotal;
  }, 0);

  const priceWithIva = Math.round(totalAmount * (1 + IVA_RATE));

  return { totalAmount, priceWithIva };
}

/**
 * Fetch a single quotation by ID with all related data
 */
export async function getQuotationById(
  id: string
): Promise<QuotationWithDetails | null> {
  const { data, error } = await supabase
    .from("quotations")
    .select(
      `
      *,
      client:clients(*),
      created_by_profile:profiles!quotations_created_by_fkey(id, name, lastname),
      sales_condition:sales_conditions(id, name, maximum_days),
      items:quotation_items(
        *,
        product:products(*)
      )
    `
    )
    .eq("id", id)
    .is("deleted_at", null)
    .single();

  if (error) {
    console.error("Error fetching quotation:", error);
    throw error;
  }

  return data as unknown as QuotationWithDetails;
}

/**
 * Fetch quotations with optional filters
 */
export async function getQuotations(filters?: {
  clientId?: string;
  status?: QuotationStatus;
  createdBy?: string;
  fromDate?: string;
  toDate?: string;
}): Promise<QuotationWithDetails[]> {
  let query = supabase
    .from("quotations")
    .select(
      `
      *,
      client:clients(id, name, tax_id),
      created_by_profile:profiles!quotations_created_by_fkey(id, name, lastname)
    `
    )
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (filters?.clientId) {
    query = query.eq("client_id", filters.clientId);
  }

  if (filters?.status) {
    query = query.eq("status", filters.status);
  }

  if (filters?.createdBy) {
    query = query.eq("created_by", filters.createdBy);
  }

  if (filters?.fromDate) {
    query = query.gte("created_at", filters.fromDate);
  }

  if (filters?.toDate) {
    query = query.lte("created_at", filters.toDate);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching quotations:", error);
    throw error;
  }

  return data as unknown as QuotationWithDetails[];
}

/**
 * Fetch quotations for a specific client
 */
export async function getQuotationsByClient(
  clientId: string
): Promise<QuotationWithDetails[]> {
  return getQuotations({ clientId });
}

/**
 * Create a new quotation with items
 */
export async function createQuotation(
  input: CreateQuotationInput,
  userId: string
): Promise<Quotation> {
  const { totalAmount, priceWithIva } = calculateTotals(input.items);

  const validUntil = dayjs()
    .add(input.validity_days || 15, "day")
    .format("YYYY-MM-DD");

  // Handle PICKUP case - convert sentinel value to null for UUID column
  const isPickup = input.delivery_address_id === PICKUP_ADDRESS_ID;
  const deliveryAddressId = isPickup ? null : input.delivery_address_id || null;

  // Insert quotation
  const { data: quotation, error: quotationError } = await supabase
    .from("quotations")
    .insert({
      client_id: input.client_id,
      created_by: userId,
      delivery_address_id: deliveryAddressId,
      delivery_street: input.delivery_street || null,
      delivery_number: input.delivery_number || null,
      delivery_city: input.delivery_city || null,
      delivery_state: input.delivery_state || null,
      delivery_comuna: input.delivery_comuna || null,
      total_amount: totalAmount,
      price_with_iva: priceWithIva,
      payment_terms: input.payment_terms,
      sales_condition_id: input.sales_condition_id || null,
      validity_days: input.validity_days || 15,
      valid_until: validUntil,
      status: "draft",
      comments: input.comments || null,
      internal_notes: input.internal_notes || null,
    })
    .select()
    .single();

  if (quotationError) {
    console.error("Error creating quotation:", quotationError);
    throw quotationError;
  }

  // Insert items
  const itemsToInsert = input.items.map((item) => ({
    quotation_id: quotation.id,
    product_id: item.product_id,
    quantity: item.quantity,
    unit_price: item.unit_price,
    discount_percent: item.discount_percent || 0,
  }));

  const { error: itemsError } = await supabase
    .from("quotation_items")
    .insert(itemsToInsert);

  if (itemsError) {
    console.error("Error creating quotation items:", itemsError);
    // Try to rollback quotation
    await supabase.from("quotations").delete().eq("id", quotation.id);
    throw itemsError;
  }

  return quotation as Quotation;
}

/**
 * Update a quotation (only allowed in draft or sent status)
 */
export async function updateQuotation(
  id: string,
  input: UpdateQuotationInput
): Promise<Quotation> {
  // Check current status
  const { data: current, error: fetchError } = await supabase
    .from("quotations")
    .select("status")
    .eq("id", id)
    .single();

  if (fetchError) throw fetchError;

  if (current.status !== "draft" && current.status !== "sent") {
    throw new Error(
      `Cannot edit quotation in status: ${current.status}. Only draft and sent quotations can be edited.`
    );
  }

  // If editing a sent quotation, revert to draft
  const newStatus =
    current.status === "sent" ? "draft" : input.status || current.status;

  // Recalculate valid_until if validity_days changed
  let validUntil = input.valid_until;
  if (input.validity_days && !input.valid_until) {
    validUntil = dayjs().add(input.validity_days, "day").format("YYYY-MM-DD");
  }

  const { data, error } = await supabase
    .from("quotations")
    .update({
      ...input,
      status: newStatus,
      valid_until: validUntil,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating quotation:", error);
    throw error;
  }

  return data as Quotation;
}

/**
 * Update quotation items (replaces all existing items)
 */
export async function updateQuotationItems(
  quotationId: string,
  items: CreateQuotationItemInput[]
): Promise<QuotationItem[]> {
  // Check status first
  const { data: quotation, error: fetchError } = await supabase
    .from("quotations")
    .select("status")
    .eq("id", quotationId)
    .single();

  if (fetchError) throw fetchError;

  if (quotation.status !== "draft" && quotation.status !== "sent") {
    throw new Error(`Cannot edit items for quotation in status: ${quotation.status}`);
  }

  // Delete existing items
  const { error: deleteError } = await supabase
    .from("quotation_items")
    .delete()
    .eq("quotation_id", quotationId);

  if (deleteError) throw deleteError;

  // Insert new items
  const itemsToInsert = items.map((item) => ({
    quotation_id: quotationId,
    product_id: item.product_id,
    quantity: item.quantity,
    unit_price: item.unit_price,
    discount_percent: item.discount_percent || 0,
  }));

  const { data, error } = await supabase
    .from("quotation_items")
    .insert(itemsToInsert)
    .select();

  if (error) throw error;

  // Update totals on quotation
  const { totalAmount, priceWithIva } = calculateTotals(items);
  await supabase
    .from("quotations")
    .update({
      total_amount: totalAmount,
      price_with_iva: priceWithIva,
      status: quotation.status === "sent" ? "draft" : quotation.status,
      updated_at: new Date().toISOString(),
    })
    .eq("id", quotationId);

  return data as QuotationItem[];
}

/**
 * Mark quotation as sent
 */
export async function markQuotationAsSent(id: string): Promise<Quotation> {
  const { data, error } = await supabase
    .from("quotations")
    .update({
      status: "sent",
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;

  return data as Quotation;
}

/**
 * Mark quotation as accepted
 */
export async function markQuotationAsAccepted(id: string): Promise<Quotation> {
  const { data, error } = await supabase
    .from("quotations")
    .update({
      status: "accepted",
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;

  return data as Quotation;
}

/**
 * Mark quotation as rejected
 */
export async function markQuotationAsRejected(
  id: string,
  reason?: string
): Promise<Quotation> {
  const { data, error } = await supabase
    .from("quotations")
    .update({
      status: "rejected",
      rejection_reason: reason || null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;

  return data as Quotation;
}

/**
 * Soft delete a quotation (only allowed in draft status)
 */
export async function deleteQuotation(id: string): Promise<boolean> {
  // Check current status
  const { data: current, error: fetchError } = await supabase
    .from("quotations")
    .select("status")
    .eq("id", id)
    .single();

  if (fetchError) throw fetchError;

  if (current.status !== "draft") {
    throw new Error("Only draft quotations can be deleted");
  }

  const { error } = await supabase
    .from("quotations")
    .update({
      deleted_at: new Date().toISOString(),
    })
    .eq("id", id);

  if (error) throw error;

  return true;
}

/**
 * Convert quotation to sales request
 */
export async function convertToSalesRequest(
  quotationId: string,
  userId: string
): Promise<string> {
  // Fetch quotation with items
  const quotation = await getQuotationById(quotationId);
  if (!quotation) {
    throw new Error("Quotation not found");
  }

  if (
    quotation.status !== "draft" &&
    quotation.status !== "sent" &&
    quotation.status !== "accepted"
  ) {
    throw new Error(`Cannot convert quotation in status: ${quotation.status}`);
  }

  // Calculate payment due date based on payment terms
  const paymentDueDate = dayjs()
    .add(quotation.payment_terms, "day")
    .toISOString();

  // Create sales request
  // Note: price_with_iva is a generated column, so we don't include it
  const { data: salesRequest, error: srError } = await supabase
    .from("sales_request")
    .insert({
      client_id: quotation.client_id,
      created_by: userId,
      status: "pending_pricing", // Needs pricing approval
      paid: false,
      payment_terms: quotation.payment_terms,
      payment_type: "future",
      payment_due_date: paymentDueDate,
      total_amount: quotation.total_amount,
      delivery_address_id: quotation.delivery_address_id,
      delivery_street: quotation.delivery_street,
      delivery_number: quotation.delivery_number,
      delivery_city: quotation.delivery_city,
      delivery_state: quotation.delivery_state,
      delivery_comuna: quotation.delivery_comuna,
      sales_condition_id: quotation.sales_condition_id,
      comments: quotation.comments,
      internal_notes: `Convertido desde presupuesto #${quotation.quotation_number}. ${quotation.internal_notes || ""}`.trim(),
    })
    .select()
    .single();

  if (srError) {
    console.error("Error creating sales request:", srError);
    throw srError;
  }

  // Create sales request items
  if (quotation.items && quotation.items.length > 0) {
    const srItems = quotation.items.map((item) => ({
      sales_request_id: salesRequest.id,
      product_id: item.product_id,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_percent: item.discount_percent || 0,
    }));

    const { error: itemsError } = await supabase
      .from("sales_request_items")
      .insert(srItems);

    if (itemsError) {
      console.error("Error creating sales request items:", itemsError);
      // Try to rollback
      await supabase.from("sales_request").delete().eq("id", salesRequest.id);
      throw itemsError;
    }
  }

  // Update quotation status to converted
  const { error: updateError } = await supabase
    .from("quotations")
    .update({
      status: "converted",
      converted_to_sales_request_id: salesRequest.id,
      converted_at: new Date().toISOString(),
      converted_by: userId,
      updated_at: new Date().toISOString(),
    })
    .eq("id", quotationId);

  if (updateError) {
    console.error("Error updating quotation status:", updateError);
    // Don't rollback sales request - it was created successfully
  }

  return salesRequest.id;
}

/**
 * Duplicate a quotation (creates a new draft copy)
 */
export async function duplicateQuotation(
  quotationId: string,
  userId: string
): Promise<Quotation> {
  const original = await getQuotationById(quotationId);
  if (!original) {
    throw new Error("Quotation not found");
  }

  const items: CreateQuotationItemInput[] =
    original.items?.map((item) => ({
      product_id: item.product_id,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_percent: item.discount_percent,
    })) || [];

  return createQuotation(
    {
      client_id: original.client_id,
      delivery_address_id: original.delivery_address_id || undefined,
      delivery_street: original.delivery_street || undefined,
      delivery_number: original.delivery_number || undefined,
      delivery_city: original.delivery_city || undefined,
      delivery_state: original.delivery_state || undefined,
      delivery_comuna: original.delivery_comuna || undefined,
      payment_terms: original.payment_terms,
      sales_condition_id: original.sales_condition_id || undefined,
      validity_days: original.validity_days,
      comments: original.comments || undefined,
      internal_notes: `Duplicado de presupuesto #${original.quotation_number}`,
      items,
    },
    userId
  );
}

/**
 * Check and expire quotations past their valid_until date
 * This should be called periodically (e.g., via cron job or on page load)
 */
export async function expireOldQuotations(): Promise<number> {
  const today = dayjs().format("YYYY-MM-DD");

  const { data, error } = await supabase
    .from("quotations")
    .update({
      status: "expired",
      updated_at: new Date().toISOString(),
    })
    .in("status", ["draft", "sent"])
    .lt("valid_until", today)
    .select();

  if (error) {
    console.error("Error expiring quotations:", error);
    throw error;
  }

  return data?.length || 0;
}
